import React from 'react';
import './GroupGeneralTab.css';
import ParticipantGroupColorPicker from './ParticipantGroupColorPicker';
import ParticipantGroupDot from '../ParticipantGroupDot';
import { ParticipantGroupChip } from '../ParticipantGroupChips';
import useRoleSettings from '../../../hooks/useRoleSettings';
import {
  COLOR_KEY_LABELS, groupColorKeys, ROLE_ORDER, roleColorKey, roleLabel,
} from '../../../util/participantGroups';
import { DESCRIPTION_MAX, NAME_MAX } from './groupAdminUtils';

// "Orga (Rot) und Admin (Gelb)" — the configured role names and colors.
const roleColorList = () => ROLE_ORDER
  .map(role => `${roleLabel(role)} (${COLOR_KEY_LABELS[roleColorKey(role)] ?? roleColorKey(role)})`)
  .join(' und ');

/** Tab "Allgemein": (create: settings source), name, plural, description, color with live preview. */
function GroupGeneralTab({ form, setField, errors, group, isCreate, copyCandidates, copySource }) {
  // Offered colors and the hint follow the configured role colors.
  useRoleSettings();
  const name = form.name.trim() || 'Neue Gruppe';
  const plural = form.pluralName.trim() || name;
  // Preview object for dot/chip (id only has to be non-null for the normalizer).
  const preview = { id: group?.id ?? 0, name, pluralName: plural, colorKey: form.colorKey, isGuest: group?.isGuest === true };
  const colorKeys = groupColorKeys(form.colorKey);
  // A role took this group's color later: it is still shown as selected, but should be changed.
  const clashingRole = ROLE_ORDER.find(role => roleColorKey(role) === form.colorKey) ?? null;

  return (
    <div className="groupTabBody">
      {isCreate && (
        <section className="groupEditorSection">
          <h4 className="groupEditorSectionTitle">Ausgangswerte</h4>
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="groupCopyFrom">Einstellungen übernehmen von</label>
            <div className="groupEditorSelectWrap">
              <select
                id="groupCopyFrom"
                className="groupEditorSelect"
                value={form.copyFromId}
                onChange={e => setField('copyFromId', e.target.value)}
              >
                <option value="">Keine – Standardwerte</option>
                {copyCandidates.map(g => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
              </select>
              <span className="material-symbols-rounded groupEditorSelectArrow" aria-hidden="true">expand_more</span>
            </div>
          </div>
          <p className="formFieldHint">
            {copySource
              ? `Standardpreise und Mail-Regeln werden von „${copySource.name}“ übernommen und sind danach änderbar.`
              : 'Übernimmt auf Wunsch Standardpreise und Mail-Regeln einer bestehenden Gruppe.'}
          </p>
        </section>
      )}

      <section className="groupEditorSection">
        <h4 className="groupEditorSectionTitle">Bezeichnung</h4>
        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor="groupName">Name</label>
          <input
            id="groupName"
            type="text"
            maxLength={NAME_MAX}
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="z. B. Mitglied"
            aria-invalid={errors.name ? 'true' : undefined}
            className={errors.name ? 'isInvalid' : ''}
          />
        </div>
        {errors.name && <p className="groupEditorFieldError">{errors.name}</p>}
        {group?.isGuest && (
          <p className="formFieldHint">Systemgruppe für alle Personen ohne Mitgliedschaft – der Name ist frei wählbar.</p>
        )}

        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor="groupPluralName">Mehrzahl</label>
          <input
            id="groupPluralName"
            type="text"
            maxLength={NAME_MAX}
            value={form.pluralName}
            onChange={e => setField('pluralName', e.target.value)}
            placeholder={form.name.trim() || 'z. B. Mitglieder'}
            aria-invalid={errors.pluralName ? 'true' : undefined}
            className={errors.pluralName ? 'isInvalid' : ''}
          />
        </div>
        {errors.pluralName && <p className="groupEditorFieldError">{errors.pluralName}</p>}
        <p className="formFieldHint">
          Wird in Texten verwendet, z. B. „Für {plural} kostenlos“. Leer = wie der Name.
        </p>

        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor="groupDescription">Beschreibung</label>
          <textarea
            id="groupDescription"
            className={`groupEditorTextarea${errors.description ? ' isInvalid' : ''}`}
            rows={3}
            maxLength={DESCRIPTION_MAX}
            value={form.description}
            onChange={e => setField('description', e.target.value)}
            placeholder="Wer gehört zu dieser Gruppe?"
          />
        </div>
        {errors.description && <p className="groupEditorFieldError">{errors.description}</p>}
        <p className="formFieldHint">Interne Notiz – nur hier im Tool sichtbar.</p>
      </section>

      <section className="groupEditorSection">
        <h4 className="groupEditorSectionTitle">Farbe</h4>
        <ParticipantGroupColorPicker value={form.colorKey} onChange={key => setField('colorKey', key)} keys={colorKeys} />
        {clashingRole && (
          <p className="groupEditorWarning">
            {COLOR_KEY_LABELS[form.colorKey]} ist die Farbe der Rolle „{roleLabel(clashingRole)}“ – Personen dieser
            Gruppe sehen dann aus wie Personen mit dieser Rolle. Bitte eine andere Farbe wählen.
          </p>
        )}
        <div className="groupColorPreview" aria-label="Vorschau">
          <span className="groupColorPreviewLabel">Vorschau</span>
          <span className="groupColorPreviewItem">
            <span className="groupColorPreviewDot"><ParticipantGroupDot participantGroup={preview} size="md" withTooltip={false} /></span>
            {name}
          </span>
          <ParticipantGroupChip group={preview} label={plural} />
          {ROLE_ORDER.map(role => (
            <span key={role} className="groupColorPreviewItem isRole">
              <span className="groupColorPreviewDot"><ParticipantGroupDot participantGroup={preview} role={role} size="md" withTooltip={false} /></span>
              {roleLabel(role)} · {name}
            </span>
          ))}
        </div>
        <p className="formFieldHint">
          {roleColorList()} werden unabhängig von der Gruppe immer in ihrer Rollenfarbe angezeigt – diese Farben
          stehen hier deshalb nicht zur Wahl. Die Rollenfarben stellst du unter „Rollen“ ein.
        </p>
      </section>
    </div>
  );
}

export default GroupGeneralTab;
